import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthChange, getUserDoc, subscribeToUser, subscribeToFamily } from '../services/firebase';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [authUser, setAuthUser] = useState(undefined); // undefined = loading
  const [userDoc, setUserDoc] = useState(null);
  const [family, setFamily] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  // Refresh subscriptions when app comes back to foreground
  // Trigger after 10 seconds in background — catches most car-ride / pocket scenarios
  useEffect(() => {
    let hiddenAt = null;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
      } else if (document.visibilityState === 'visible') {
        const backgroundTime = hiddenAt ? Date.now() - hiddenAt : 0;
        if (backgroundTime > 10000) { // 10 seconds
          setLoading(true); // signal to child components that fresh data is incoming
          setRefreshKey(k => k + 1);
        }
        hiddenAt = null;
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    const unsubAuth = onAuthChange(async (firebaseUser) => {
      setAuthUser(firebaseUser);
      if (!firebaseUser) {
        setUserDoc(null);
        setFamily(null);
        setLoading(false);
      }
    });
    return unsubAuth;
  }, []);

  // Subscribe to user doc when auth user changes
  useEffect(() => {
    if (!authUser) return;
    const unsub = subscribeToUser(authUser.uid, (doc) => {
      setUserDoc(doc);
      setLoading(false);
    });
    return unsub;
  }, [authUser, refreshKey]);

  // Subscribe to family doc when user doc has a familyId
  useEffect(() => {
    if (!userDoc?.familyId) {
      setFamily(null);
      return;
    }
    const unsub = subscribeToFamily(userDoc.familyId, (doc) => {
      setFamily(doc);
    });
    return unsub;
  }, [userDoc?.familyId, refreshKey]);

  const isParent = userDoc?.role === 'parent';
  const isStudent = userDoc?.role === 'student';
  const isEnrolled = !!userDoc?.familyId;

  return (
    <AuthContext.Provider value={{
      authUser,
      userDoc,
      family,
      loading,
      isParent,
      isStudent,
      isEnrolled,
      refreshKey,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
